import { type RefObject, useCallback, useEffect, useRef, useState } from "react";
import { formatClock } from "../lib/format";
import { cn } from "../lib/utils";
import { previewLeft } from "./preview-position";
import { type SpriteSheet, cueAt, parseSpriteVtt } from "./sprite-preview";

/**
 * Pasek przewijania z podglądem klatki.
 *
 * Powstał, bo natywne `<video controls>` **nie umie pokazać podglądu** — a bez
 * podglądu przewijanie jest zgadywaniem: trafienie w koniec openingu albo
 * w scenę, którą ktoś przegapił, wymaga kilku prób i za każdą płaci się skokiem
 * obrazu. Przy okazji natywny pasek ma na telefonie cel wysokości kilku pikseli.
 *
 * **Krok trzeci: dopiero puszczenie palca przewija.** Przewijanie na każdy ruch
 * palca po pasku wysyła dziesiątki żądań częściowych na sekundę i przy pliku
 * z serwera kończy się zatrzymaniem obrazu na kilka sekund. W trakcie ruchu
 * pokazujemy tylko podgląd i czas — to daje ten sam wynik bez obciążenia.
 */

/**
 * Powiększenie kafla w podglądzie.
 *
 * Kafel ma 160 px szerokości, co nad paskiem jest za małe, żeby rozpoznać scenę.
 * Skalowanie w przeglądarce jest tu właściwe: większy kafel znaczyłby cięższy
 * arkusz dla wszystkich, a podgląd oglądany przez pół sekundy nie potrzebuje
 * ostrości.
 */
const PREVIEW_SCALE = 1.25;

/**
 * Wysokość samego paska. Pole dotknięcia jest znacznie wyższe (patrz `py-3`).
 *
 * Pasek **grubieje pod kursorem i przy fokusie**, bo szyna 1,5 piksela jest celem,
 * w który trudno trafić myszą przy pełnym ekranie 4K — a jednocześnie cienka szyna
 * jest właściwa, gdy nikt jej nie używa: ma nie zabierać obrazu. Rośnie sama szyna,
 * nie pole dotknięcia, więc nic nie przeskakuje w układzie.
 */
const TRACK_CLASSES = cn(
  "relative h-1.5 w-full rounded-full bg-foreground/25",
  "transition-[height] duration-150 ease-tv",
  "group-hover/seek:h-2.5 group-focus-visible/seek:h-2.5",
);

export function SeekBar({
  videoRef,
  durationSec,
  spriteVttUrl,
  disabled = false,
}: {
  videoRef: RefObject<HTMLVideoElement | null>;
  /** Czas z bazy; element wideo może go jeszcze nie znać. */
  durationSec: number | null;
  /** Adres pliku VTT z wycinkami. `null`, gdy materiał nie ma arkusza. */
  spriteVttUrl: string | null;
  /** Follower w pokoju widzi postęp, ale nie przewija — o pozycji decyduje prowadzący. */
  disabled?: boolean;
}) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [current, setCurrent] = useState(0);
  const [buffered, setBuffered] = useState(0);
  const [sheet, setSheet] = useState<SpriteSheet | null>(null);
  /** Pozycja pod kursorem albo palcem w sekundach; `null` gdy nikt nie wskazuje. */
  const [hoverSec, setHoverSec] = useState<number | null>(null);
  const [scrubbing, setScrubbing] = useState(false);

  const duration =
    durationSec !== null && durationSec > 0
      ? durationSec
      : (videoRef.current?.duration ?? 0) || null;

  /* Postęp i bufor. `progress` zdarza się rzadziej niż `timeupdate`, więc
     osobne nasłuchy zamiast jednego stopera. */
  useEffect(() => {
    const video = videoRef.current;
    if (video === null) return;

    const onTime = () => setCurrent(video.currentTime);
    const onProgress = () => {
      // Interesuje nas koniec zakresu ZAWIERAJĄCEGO bieżącą pozycję, a nie
      // ostatni zakres w ogóle: po przewinięciu wstecz istnieją dwa i ten
      // późniejszy nie mówi nic o tym, co da się odtworzyć teraz.
      const ranges = video.buffered;
      for (let i = 0; i < ranges.length; i += 1) {
        if (ranges.start(i) <= video.currentTime && video.currentTime <= ranges.end(i)) {
          setBuffered(ranges.end(i));
          return;
        }
      }
    };

    video.addEventListener("timeupdate", onTime);
    video.addEventListener("progress", onProgress);
    video.addEventListener("seeked", onTime);
    return () => {
      video.removeEventListener("timeupdate", onTime);
      video.removeEventListener("progress", onProgress);
      video.removeEventListener("seeked", onTime);
    };
  }, [videoRef]);

  /* Arkusz miniatur. Brak pliku to normalny stan: powstaje minutę po gotowości
     materiału, więc przy pierwszym odtworzeniu może go jeszcze nie być. */
  useEffect(() => {
    if (spriteVttUrl === null) {
      setSheet(null);
      return;
    }
    let cancelled = false;
    fetch(spriteVttUrl, { credentials: "same-origin" })
      .then((r) => (r.ok ? r.text() : null))
      .then((text) => {
        if (cancelled || text === null) return;
        setSheet(parseSpriteVtt(text, new URL(spriteVttUrl, window.location.href).toString()));
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [spriteVttUrl]);

  /** Sekunda odpowiadająca punktowi na pasku. */
  const secondsAt = useCallback(
    (clientX: number): number | null => {
      const track = trackRef.current;
      if (track === null || duration === null) return null;
      const box = track.getBoundingClientRect();
      if (box.width === 0) return null;
      const ratio = Math.min(1, Math.max(0, (clientX - box.left) / box.width));
      return ratio * duration;
    },
    [duration],
  );

  const commit = useCallback(
    (seconds: number) => {
      const video = videoRef.current;
      if (video === null) return;
      video.currentTime = seconds;
      setCurrent(seconds);
    },
    [videoRef],
  );

  if (duration === null) {
    // Bez znanego czasu trwania pasek nie ma skali. Pokazujemy samą szynę, żeby
    // sterowanie nie podskakiwało w chwili, gdy czas wreszcie dojdzie.
    return <div className={TRACK_CLASSES} aria-hidden="true" />;
  }

  const percent = (current / duration) * 100;
  const bufferedPercent = (Math.max(buffered, current) / duration) * 100;
  const previewSec = hoverSec ?? null;
  const cue = sheet !== null && previewSec !== null ? cueAt(sheet, previewSec) : null;

  return (
    <div className="relative w-full">
      {/*
        Podgląd nad paskiem. Skalujemy kafel przez `background-size`, bo obraz
        jest arkuszem: wycinek pokazuje `background-position` przeliczone tą samą
        skalą, inaczej widać sąsiednie kafle.
      */}
      {cue !== null && sheet !== null && previewSec !== null && (
        <div
          className="pointer-events-none absolute bottom-full z-20 mb-3 -translate-x-1/2 overflow-hidden rounded-lg border border-border bg-black shadow-2xl shadow-black/60"
          // Pozycja przycięta do krawędzi paska — patrz `preview-position.ts`.
          // Bez tego podgląd w pierwszej i ostatniej minucie wychodził za kadr,
          // a kontener obcina wszystko, co za niego wyjdzie.
          style={{ left: previewLeft(previewSec / duration, Math.round(cue.w * PREVIEW_SCALE)) }}
        >
          {/*
            Dwa pojemniki, bo skalowanie NIE zmienia miejsca zajmowanego w układzie:
            zewnętrzny ma wymiar po powiększeniu, wewnętrzny wymiar kafla i samą
            skalę. Bez tego ramka podglądu obcięłaby powiększony obraz.
          */}
          <div
            style={{
              width: `${Math.round(cue.w * PREVIEW_SCALE)}px`,
              height: `${Math.round(cue.h * PREVIEW_SCALE)}px`,
              overflow: "hidden",
            }}
          >
            <div
              style={{
                width: `${cue.w}px`,
                height: `${cue.h}px`,
                backgroundImage: `url(${sheet.imageUrl})`,
                // Wycinek arkusza. Rozmiar tła zostaje naturalny — procenty
                // odnosiłyby się do okienka podglądu, nie do arkusza.
                backgroundPosition: `-${cue.x}px -${cue.y}px`,
                backgroundSize: "auto",
                transform: `scale(${PREVIEW_SCALE})`,
                transformOrigin: "top left",
              }}
            />
          </div>
          <span className="block bg-background/90 py-0.5 text-center text-xs tabular-nums">
            {formatClock(previewSec)}
          </span>
        </div>
      )}

      {/*
        Pole dotknięcia jest wyższe od paska o cały odstęp: pasek grubości sześciu
        pikseli jest celem chybianym palcem, a pogrubienie samego paska zajęłoby
        obraz. `data-slot` wyłącza globalne powiększanie celów i pozwala skrótom
        klawiszowym rozpoznać, że strzałki obsługuje już ten element.
      */}
      <div
        data-slot="seek-bar"
        role="slider"
        tabIndex={disabled ? -1 : 0}
        aria-label="Pozycja odtwarzania"
        aria-valuemin={0}
        aria-valuemax={Math.round(duration)}
        aria-valuenow={Math.round(current)}
        aria-valuetext={`${formatClock(current)} z ${formatClock(duration)}`}
        aria-disabled={disabled}
        // `group/seek` z nazwą, a nie samo `group`: kadr ma już własne grupy (kafle,
        // przyciski), a nienazwana zagnieżdżona grupa przejmuje ich zdarzenia.
        className="focus-ring-own group/seek cursor-pointer touch-none py-3 outline-none focus-visible:ring-[0.1875rem] focus-visible:ring-ring/50"
        onPointerDown={(e) => {
          if (disabled) return;
          // Przechwycenie wskaźnika: ruch poza pasek ma dalej przesuwać podgląd,
          // inaczej przewijanie urywa się przy wyjechaniu palcem za krawędź.
          e.currentTarget.setPointerCapture(e.pointerId);
          setScrubbing(true);
          setHoverSec(secondsAt(e.clientX));
        }}
        onPointerMove={(e) => {
          if (disabled) return;
          if (!scrubbing && e.pointerType !== "mouse") return;
          setHoverSec(secondsAt(e.clientX));
        }}
        onPointerUp={(e) => {
          if (disabled || !scrubbing) return;
          const seconds = secondsAt(e.clientX);
          setScrubbing(false);
          setHoverSec(null);
          if (seconds !== null) commit(seconds);
        }}
        onPointerCancel={() => {
          setScrubbing(false);
          setHoverSec(null);
        }}
        onPointerLeave={() => {
          if (!scrubbing) setHoverSec(null);
        }}
        onKeyDown={(e) => {
          if (disabled) return;
          const step = e.shiftKey ? 60 : 10;
          if (e.key === "ArrowLeft") {
            e.preventDefault();
            commit(Math.max(0, current - step));
          }
          if (e.key === "ArrowRight") {
            e.preventDefault();
            commit(Math.min(duration, current + step));
          }
          if (e.key === "Home") {
            e.preventDefault();
            commit(0);
          }
          if (e.key === "End") {
            e.preventDefault();
            commit(duration);
          }
        }}
      >
        <div ref={trackRef} className={TRACK_CLASSES}>
          {/* Bufor pod postępem: pokazuje, dokąd da się przewinąć bez czekania. */}
          <div
            className="absolute inset-y-0 left-0 rounded-full bg-foreground/20"
            style={{ width: `${bufferedPercent}%` }}
          />
          <div
            className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-primary to-accent"
            style={{ width: `${percent}%` }}
          />
          {!disabled && (
            /**
             * Kropka pozycji.
             *
             * **Wyśrodkowanie i powiększenie MUSZĄ iść osobnymi własnościami.**
             * Tailwind 4 realizuje `-translate-x-1/2` przez własność `translate`
             * (widać to w zbudowanym arkuszu: `translate: var(--tw-translate-x)
             * var(--tw-translate-y)`), a nie przez `transform`. Wcześniejsza wersja
             * miała klasy centrujące **i** własne `transform: translate(-50%, -50%)`,
             * czyli obie własności naraz — a one się **składają**. Kropka przesuwała
             * się o całą swoją wysokość zamiast o połowę i lądowała ~11 px NAD paskiem,
             * przy 6-pikselowej szynie wyglądając jak element z innego układu.
             *
             * Dlatego przesunięcie zostaje w klasach (`translate`), a nacisk przy
             * przewijaniu idzie własnością `scale`. `transition-transform` w tej wersji
             * Tailwinda animuje `transform, translate, scale, rotate`, więc płynność
             * zostaje.
             */
            <div
              className="absolute top-1/2 size-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-foreground shadow-md transition-transform duration-150 ease-tv"
              style={{
                left: `${scrubbing && hoverSec !== null ? (hoverSec / duration) * 100 : percent}%`,
                scale: scrubbing ? "1.3" : "1",
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
}
